import { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import { LanguageContext } from "../../contexts/LanguageProvider";
import { API_ENDPOINTS } from "../../configs/apiConfig";
import { Card, Button, Stack, Badge } from "react-bootstrap";
import ProductModal from "./ProductModal";
import LinkProductModal from "./LinkProductModal";
import { FiLink, FiEye } from "react-icons/fi";
import { FiPlusCircle, FiFilter, FiGrid } from "react-icons/fi";
import { RiDeleteBin2Line } from "react-icons/ri";
import { Modal } from "react-bootstrap";
import translations from "./translations"; // Import translations
import useMediaQuery from "../../utils/useMediaQuery"; // Import the custom hook
import { useAlertContext } from '../../contexts/AlertContext';
import PaginationComponent from "../PaginationComponent";
import BrandSelect from "../common/BrandSelect";

function ProductList() {
  const { language } = useContext(LanguageContext);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterActiveIngredient, setFilterActiveIngredient] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [productsPerPage, setProductsPerPage] = useState(10);

  // State for modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  const { showSuccess, showError } = useAlertContext();

  const t = translations[language] || translations.pt;
  
  // Constante para detectar dispositivos móveis
  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    setProductsPerPage(isMobile ? 5 : 10);
    setCurrentPage(1); // Reseta para a primeira página ao mudar productsPerPage
  }, [isMobile]);
  
  // Carrega os produtos
  const fetchProducts = useCallback( async () => {
    try {
      const auth = localStorage.getItem("auth");
      const response = await axios.get(API_ENDPOINTS.PRODUCTS_LIST, {
        headers: {
          Authorization: `Basic ${auth}`,
        },
      });
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
      showError(t.failedToFetch);
    }
  }, [showError, t.failedToFetch]);
  
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Função para aplicar os filtros
  const applyFilters = () => {
    let filtered = [...products];
    if(isMobile)
      {
        setProductsPerPage(5);
      }
    if (filterType) {
      filtered = filtered.filter((product) => product.type === filterType);
    }

    if (filterName && filterName.trim() !== "") {
      filtered = filtered.filter((product) =>
        product.name.toLowerCase().includes(filterName.toLowerCase())
      );
    }

    if (filterActiveIngredient && filterActiveIngredient.trim() !== "") {
      filtered = filtered.filter((product) =>
        product.activeIngredient
          .toLowerCase()
          .includes(filterActiveIngredient.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
    setCurrentPage(1);
  };

  // Function to open modal in ADD mode
  const handleAddProduct = () => {
    setIsAddMode(true);
    setSelectedProduct(null);
    setIsModalOpen(true);
  };

  // Function to open modal in EDIT mode
  const handleEdit = (product) => {
    setIsAddMode(false);
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleLink = (product) => {
    setIsAddMode(false);
    setSelectedProduct(product);
    setIsLinkModalOpen(true);
  };

  // Function to close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  // Function to close link modal
  const closeLinkModal = () => {
    setIsLinkModalOpen(false);
    setSelectedProduct(null);
  };

  // Function to save product (handles both add and update)
  const handleSaveProduct = async (formData) => {
    try {
      const auth = localStorage.getItem("auth");
      if (isAddMode) {
        // Add new product
        await axios.post(
          API_ENDPOINTS.PRODUCTS_REGISTER,
          formData,
          {
            headers: { Authorization: `Basic ${auth}` },
          }
        );
        await fetchProducts();
        showSuccess(t.successAdd);
      } else {
        // Update existing product
        await axios.put(
          API_ENDPOINTS.PRODUCTS_UPDATE(selectedProduct.id),
          formData,
          {
            headers: { Authorization: `Basic ${auth}` },
          }
        );
        await fetchProducts();
        showSuccess(t.successUpdate);
      }

      setTimeout(closeModal, 0);
    } catch (error) {
      showError(isAddMode ? t.errorAdd : t.errorUpdate);
    }
  };

  // Lógica de paginação
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  // Função para deletar um produto
  const handleDelete = async (productId) => {
    try {
      const auth = localStorage.getItem("auth");
      await axios.delete(API_ENDPOINTS.PRODUCTS_DELETE(productId), {
        headers: { Authorization: `Basic ${auth}` },
      });
      setProducts(products.filter((product) => product.id !== productId));
      applyFilters();
      showSuccess(t.successDelete);
    } catch (error) {
      showError(t.errorDelete);
    }
    setShowDeleteConfirm(false);
    setProductToDelete(null);
  };

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setShowDeleteConfirm(true);
  };

  // Função para lidar com a tecla Enter nos filtros
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      applyFilters();
    }
  };

  return (
    <div className="page-section">
      <Card>
        <Card.Header as="h2">
          <Stack direction="horizontal" gap={3} className="w-100">
            <div className="d-inline-flex align-items-center gap-2"><FiGrid className="text-primary" /> {t.productList}</div>
            <div className="ms-auto">
              <Button variant="success" onClick={handleAddProduct}>
                <FiPlusCircle className="me-1" /> {t.addProduct}
              </Button>
            </div>
          </Stack>
        </Card.Header>

        <Card.Body>
          {/* Filtros */}
          <Card className="mb-4">
            <Card.Header as="h4">
              <FiFilter className="me-2" /> Filtros
            </Card.Header>
            <Card.Body className="p-3">
              <div className="row align-items-end">
                <div className="col-md-3">
                  <label className="form-label">{t.typeFilter}</label>
                  <BrandSelect
                    className="w-100"
                    value={filterType}
                    onChange={(v) => setFilterType(v)}
                    options={[
                      { value: '', label: t.all },
                      { value: 'FERTILIZER', label: t.fertilizer },
                      { value: 'PESTICIDE', label: t.pesticide },
                    ]}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">{t.nameFilter}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t.placeholderName}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">
                    {t.activeIngredientFilter}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={filterActiveIngredient}
                    onChange={(e) => setFilterActiveIngredient(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t.placeholderActiveIngredient}
                  />
                </div>
                <div className="col-md-3 mt-3">
                  <Button
                    variant="primary"
                    className="w-100"
                    onClick={applyFilters}
                  >
                    {t.searchButton}
                  </Button>
                </div>
              </div>
              <div className="row mt-3"></div>
            </Card.Body>
          </Card>

          {/* Tabela de produtos */}
          <Card>
            <Card.Header as="h4">
              <FiGrid className="me-2" /> Produtos
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <table className="table table-striped mb-0">
                  <thead>
                    <tr>
                      <th>{t.name}</th>
                      {!isMobile && (
                        <>
                          <th>{t.activeIngredient}</th>
                          <th>{t.productType}</th>
                        </>
                      )}
                      <th>{t.actions}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentProducts.length > 0 ? (
                      currentProducts.map((product) => (
                        <tr key={product.id}>
                          <td>
                            <div>{product.name}</div>
                            {isMobile && (
                              <div className="mt-1 p-2">
                                <small className="text-muted d-block">
                                  {product.activeIngredient}
                                </small>
                                <Badge
                                  bg={
                                    product.type === "FERTILIZER"
                                      ? "success"
                                      : "primary"
                                  }
                                  className="mt-1"
                                >
                                  {product.type === "FERTILIZER"
                                    ? t.fertilizer
                                    : t.pesticide}
                                </Badge>
                              </div>
                            )}
                          </td>
                          {!isMobile && (
                            <>
                              <td>{product.activeIngredient}</td>
                              <td>
                                <Badge
                                  bg={
                                    product.type === "FERTILIZER"
                                      ? "success"
                                      : "primary"
                                  }
                                >
                                  {product.type === "FERTILIZER"
                                    ? t.fertilizer
                                    : t.pesticide}
                                </Badge>
                              </td>
                            </>
                          )}
                          <td>
                            <Stack direction={isMobile ? "vertical" : "horizontal"} gap={2}>
                              <Button
                                size="sm"
                                title={t.view}
                                onClick={() => handleEdit(product)}
                                style={{
                                  width: isMobile ? "100%" : "25px",
                                  height: "25px",
                                  padding: "0",
                                  margin: "0",
                                  color: "#fff",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <FiEye size={18} />
                                {isMobile && <span className="ms-2">{t.view}</span>}
                              </Button>
                              <Button
                                size="sm"
                                title={t.link}
                                onClick={() => handleLink(product)}
                                style={{
                                  width: isMobile ? "100%" : "25px",
                                  height: "25px",
                                  padding: "0",
                                  margin: "0",
                                  color: "#fff",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <FiLink size={18} />
                                {isMobile && <span className="ms-2">{t.link}</span>}
                              </Button>
                              <Button
                                size="sm"
                                title={t.delete}
                                onClick={() => handleDeleteClick(product)}
                                style={{
                                  width: isMobile ? "100%" : "25px",
                                  height: "25px",
                                  padding: "0",
                                  margin: "0",
                                  color: "#fff",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <RiDeleteBin2Line size={18} />
                                {isMobile && <span className="ms-2">{t.delete}</span>}
                              </Button>
                            </Stack>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={isMobile ? "2" : "4"} className="text-center">
                          {t.noProducts}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>

          <ProductModal
            isOpen={isModalOpen}
            onClose={closeModal}
            onSave={handleSaveProduct}
            initialData={selectedProduct}
            translations={t}
            isAddMode={isAddMode}
          />

          <LinkProductModal
            isOpen={isLinkModalOpen}
            onClose={closeLinkModal}
            productId={selectedProduct?.id}
            productName={selectedProduct?.name}
          />

          {/* Paginação Responsiva */}
          {currentProducts.length > 0 && 
          <PaginationComponent
            totalPages={totalPages}
            isMobile={isMobile}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            t={t}
            tItemName={t.products}
            indexOfFirst={indexOfFirstProduct}
            indexOfLast={indexOfLastProduct}
            lengthTotal={filteredProducts.length}
          />}
        </Card.Body>
      </Card>

      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t.confirmDelete}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t.confirmDeleteMessage}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteConfirm(false)}
          >
            {t.cancelButton}
          </Button>
          <Button
            variant="danger"
            onClick={() => handleDelete(productToDelete.id)}
          >
            {t.delete}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ProductList;

